/* eslint-disable @typescript-eslint/no-explicit-any */

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

type QueryValue = string | number | boolean | null | undefined

type HttpOptions = {
    headers?: Record<string, string>;
    responseType?: 'blob' | 'text' | 'json';
    params?: Record<string, QueryValue | QueryValue[]>;
}

type HttpResponse = {
    data: any;
    body: any;
    response: {
        data: any;
        body: any;
        status: number;
        statusText: string;
        headers: Headers;
        url: string;
    };
    ok: boolean;
    status: number;
    statusText: string;
    headers: Headers;
    url: string;
    json: () => Promise<any>;
    text: () => Promise<string>;
    blob: () => Promise<Blob>;
}

type StatusHandler = (status: number) => void

let statusHandler: StatusHandler | null = null

export function setHttpStatusHandler(handler: StatusHandler) {
    statusHandler = handler
}

function buildHeaders(body?: any, options?: HttpOptions) {
    const headers = new Headers(options?.headers)
    headers.set('clientVersion', process.env.VUE_APP_VERSION || '')

    if (body !== undefined && !(body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json')
    }

    return headers
}

function buildBody(body?: any) {
    if (body === undefined || body instanceof FormData || typeof body === 'string') {
        return body
    }

    return JSON.stringify(body)
}

function buildUrl(url: string, options?: HttpOptions) {
    if (!options?.params) {
        return url
    }

    const hashIndex = url.indexOf('#')
    const baseUrl = hashIndex >= 0 ? url.substring(0, hashIndex) : url
    const hash = hashIndex >= 0 ? url.substring(hashIndex) : ''
    const searchParams = new URLSearchParams()

    Object.keys(options.params).forEach(key => {
        const value = options.params ? options.params[key] : undefined
        const values = Array.isArray(value) ? value : [value]

        values.forEach(item => {
            if (item !== null && item !== undefined) {
                searchParams.append(key, String(item))
            }
        })
    })

    const query = searchParams.toString()

    if (!query) {
        return url
    }

    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${query}${hash}`
}

async function readData(response: Response, options?: HttpOptions) {
    if (options?.responseType === 'blob') {
        return response.blob()
    }

    if (options?.responseType === 'text') {
        return response.text()
    }

    const text = await response.text()

    if (!text) {
        return null
    }

    try {
        return JSON.parse(text)
    } catch {
        return text
    }
}

function createCompatResponse(response: Response, data: any): HttpResponse {
    const body = response.ok && (data === null || data === undefined) ? {} : data

    return {
        data,
        body,
        response: {
            data,
            body,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            url: response.url
        },
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        url: response.url,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(typeof data === 'string' ? data : JSON.stringify(data)),
        blob: () => Promise.resolve(data instanceof Blob ? data : new Blob([typeof data === 'string' ? data : JSON.stringify(data)]))
    }
}

async function request(method: HttpMethod, url: string, body?: any, options?: HttpOptions) {
    const requestUrl = buildUrl(url, options)

    try {
        const response = await fetch(requestUrl, {
            method,
            headers: buildHeaders(body, options),
            body: method === 'GET' ? undefined : buildBody(body),
            credentials: 'same-origin'
        })

        statusHandler?.(response.status)

        const data = await readData(response, options)
        const compatResponse = createCompatResponse(response, data)

        if (!response.ok) {
            return Promise.reject(compatResponse)
        }

        return compatResponse
    } catch (error) {
        if (error instanceof TypeError) {
            statusHandler?.(0)

            const data = { erro: error.message }
            const networkError = error as any

            networkError.data = data
            networkError.body = data
            networkError.response = {
                data,
                body: data,
                status: 0,
                statusText: '',
                headers: new Headers(),
                url: requestUrl
            }
        }

        return Promise.reject(error)
    }
}

const http = {
    get(url: string, options?: HttpOptions) {
        return request('GET', url, undefined, options)
    },
    post(url: string, body?: any, options?: HttpOptions) {
        return request('POST', url, body, options)
    },
    put(url: string, body?: any, options?: HttpOptions) {
        return request('PUT', url, body, options)
    },
    delete(url: string, options?: HttpOptions) {
        return request('DELETE', url, undefined, options)
    }
}

export default http
